import React, { useMemo } from 'react';
import { ResponsivePie } from '@nivo/pie';
import {
    CONSUMPTION_GRADIENT,
    colorsAlongGradient,
    DIM_TARGET,
    DARK_TEXT,
    FONT_STACK,
    formatValue,
    foldIntoOthers,
    mixColor,
} from '../shared/chartTheme.js';
import '../../styles/DashboardComponents.css';

// Total au centre du donut, en <text> SVG (couche custom Nivo) plutôt qu'en overlay
// HTML : même parti pris que UsageTaxonomySunburstReportCard (cohérent à l'export/copie).
const CenterLabel = ({ centerX, centerY, total, unit }) => (
    <text
        x={centerX}
        y={centerY}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: 26, fontWeight: 700, fontFamily: FONT_STACK, fill: DARK_TEXT }}
    >
        {formatValue(total)} {unit}
    </text>
);

/* Donut Nivo de "Répartition des consommations" (haut du dashboard bâtiment) —
   remplace l'ancien donut Chart.js (WaterUsageChart.js) uniquement à cet endroit :
   WaterUsageChart.js reste utilisé tel quel par ClientDashboard.js et les pages
   d'action plan, qui ont besoin de sa prop `totalInitConsumption`. */
const UsageDonutChartNivo = ({ data, unit, onSegmentClick, selectedLabel = null }) => {
    const { labels, values } = foldIntoOthers(Object.keys(data), Object.values(data));
    const total = Math.round(values.reduce((sum, v) => sum + v, 0));

    const pieData = useMemo(() => {
        const colors = colorsAlongGradient(labels, CONSUMPTION_GRADIENT, (label) => label === 'Autres');
        return labels.map((label, i) => {
            const dimmed = selectedLabel && label !== selectedLabel;
            return {
                id: label,
                label,
                value: values[i],
                color: dimmed ? mixColor(colors[i], DIM_TARGET, 0.55) : colors[i],
            };
        });
    }, [labels, values, selectedLabel]);

    return (
        <div className="water-usage-chart-container usage-donut-chart-nivo">
            <div className="nivo-chart-fill">
                <ResponsivePie
                    data={pieData}
                    id="id"
                    value="value"
                    valueFormat={(v) => `${formatValue(v)} ${unit}`}
                    innerRadius={0.72}
                    padAngle={1}
                    cornerRadius={2}
                    colors={(d) => d.data.color}
                    borderWidth={0}
                    enableArcLabels={false}
                    enableArcLinkLabels={false}
                    animate={true}
                    motionConfig="gentle"
                    theme={{ text: { fontFamily: FONT_STACK } }}
                    onClick={(datum) => onSegmentClick?.(datum.id)}
                    layers={['arcs', (props) => <CenterLabel {...props} total={total} unit={unit} />]}
                    tooltip={({ datum }) => (
                        <div className="sunburst-tooltip">
                            <div className="sunburst-tooltip-head">
                                <span className="sunburst-tooltip-swatch" style={{ background: datum.data.color }} />
                                <div className="sunburst-tooltip-label">{datum.label}</div>
                            </div>
                            <div className="sunburst-tooltip-value">
                                {formatValue(datum.value)} {unit} ({total > 0 ? ((datum.value / total) * 100).toFixed(1) : '0.0'}%)
                            </div>
                        </div>
                    )}
                />
            </div>
        </div>
    );
};

export default UsageDonutChartNivo;
